import { useState } from "react";
import { Sun, RotateCcw, Copy, CheckCircle } from "lucide-react";

const TEMPLATES = [
  { category: "Self-Worth", templates: [
    "I am worthy of love and respect, exactly as I am.",
    "I trust myself to make good decisions.",
    "My worth is not defined by my productivity.",
    "I am enough, even on my hardest days.",
  ]},
  { category: "Resilience", templates: [
    "I have overcome challenges before, and I can do it again.",
    "Setbacks are setup for comebacks.",
    "I am stronger than my doubts.",
    "Every day I am growing and evolving.",
  ]},
  { category: "Calm", templates: [
    "I release what I cannot control.",
    "I choose peace over perfection.",
    "This moment is temporary. I am safe.",
    "I give myself permission to rest.",
  ]},
  { category: "Purpose", templates: [
    "My life has meaning beyond my work.",
    "I am making a difference, even in small ways.",
    "I choose to focus on what matters most to me.",
    "I am building a life I love, one step at a time.",
  ]},
];

const AffirmationBuilder = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeAffirmation, setActiveAffirmation] = useState(null);
  const [customAffirmation, setCustomAffirmation] = useState("");
  const [saved, setSaved] = useState([]);
  const [copied, setCopied] = useState(false);

  const saveAffirmation = (text) => {
    if (!saved.includes(text)) setSaved([...saved, text]);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(saved.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="affirmation-builder" className="rounded-2xl p-8" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
      <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Affirmation Builder</h2>
      <p className="text-sm mb-6" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
        Positive affirmations rewire neural pathways over time. Choose affirmations that resonate with you, or write your own.
      </p>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-6">
        {TEMPLATES.map((t) => (
          <button key={t.category} onClick={() => setSelectedCategory(selectedCategory === t.category ? null : t.category)}
            className="rounded-full px-4 py-2 text-xs font-medium transition-all"
            style={{
              background: selectedCategory === t.category ? "var(--ll-cta)" : "transparent",
              color: selectedCategory === t.category ? "var(--ll-cta-text)" : "var(--ll-text-muted)",
              border: `1px solid ${selectedCategory === t.category ? "var(--ll-cta)" : "var(--ll-border)"}`,
              fontFamily: "'Outfit', sans-serif",
            }}>
            {t.category}
          </button>
        ))}
      </div>

      {/* Affirmations */}
      {selectedCategory && (
        <div className="space-y-2 mb-6">
          {TEMPLATES.find((t) => t.category === selectedCategory)?.templates.map((aff) => (
            <button key={aff} onClick={() => saveAffirmation(aff)}
              className="w-full text-left p-4 rounded-xl transition-all hover:-translate-y-0.5 flex items-start gap-3"
              style={{ background: saved.includes(aff) ? "rgba(163,184,153,0.15)" : "var(--ll-bg-secondary)", border: `1px solid ${saved.includes(aff) ? "var(--ll-green)" : "transparent"}` }}>
              <Sun size={14} className="mt-0.5 flex-shrink-0" style={{ color: saved.includes(aff) ? "var(--ll-green)" : "var(--ll-accent)" }} />
              <span className="text-sm" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{aff}</span>
              {saved.includes(aff) && <CheckCircle size={14} className="ml-auto flex-shrink-0" style={{ color: "var(--ll-green)" }} />}
            </button>
          ))}
        </div>
      )}

      {/* Custom */}
      <div className="p-4 rounded-xl mb-6" style={{ background: "var(--ll-bg-secondary)" }}>
        <p className="text-xs font-medium mb-2" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Write your own</p>
        <div className="flex gap-2">
          <input data-testid="custom-affirmation" value={customAffirmation} onChange={(e) => setCustomAffirmation(e.target.value)}
            placeholder="I am..." className="flex-1 rounded-xl px-3 py-2 text-sm border outline-none" style={{ borderColor: "var(--ll-border)", fontFamily: "'Outfit', sans-serif" }} />
          <button onClick={() => { if (customAffirmation.trim()) { saveAffirmation(customAffirmation); setCustomAffirmation(""); } }}
            className="rounded-xl px-4 py-2 text-xs font-medium" style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
            Save
          </button>
        </div>
      </div>

      {/* Saved */}
      {saved.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Your affirmations ({saved.length})</p>
            <button onClick={copyAll} className="text-xs flex items-center gap-1 transition-colors hover:opacity-70" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              {copied ? <><CheckCircle size={12} /> Copied!</> : <><Copy size={12} /> Copy all</>}
            </button>
          </div>
          <div className="space-y-2">
            {saved.map((s) => (
              <div key={s} className="flex items-center gap-2 p-3 rounded-xl" style={{ background: "rgba(247,208,70,0.06)" }}>
                <Sun size={12} style={{ color: "var(--ll-accent)" }} />
                <p className="text-sm" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AffirmationBuilder;
